import { supabase } from '../../../shared/services/supabase';

export interface User {
  id: string;
  email: string;
  name: string;
  phone_number: string | null;
  sms_opted_in_at: string | null;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserWithLocations extends User {
  locations: Array<{
    id: string;
    name: string;
    short_code: string;
  }>;
}

export interface CreateUserInput {
  email: string;
  name: string;
  location_ids: string[];
  is_admin?: boolean;
}

export interface UpdateUserInput {
  id: string;
  name?: string;
  is_active?: boolean;
  is_admin?: boolean;
}

export interface UpdateUserLocationsInput {
  user_id: string;
  location_ids: string[];
}

export const usersService = {
  /**
   * Fetch all users with their location assignments
   */
  async getUsers(): Promise<UserWithLocations[]> {
    const { data: users, error: usersError } = await supabase
      .from('users')
      .select('*')
      .order('name', { ascending: true });

    if (usersError) {
      console.error('Error fetching users:', usersError);
      throw new Error(usersError.message);
    }

    // Fetch location assignments for all users
    const { data: userLocations, error: locationsError } = await supabase
      .from('user_locations')
      .select(
        `
        user_id,
        locations (
          id,
          name,
          short_code
        )
      `
      )
      .in(
        'user_id',
        // @ts-expect-error - Database types not regenerated yet
        users.map((u) => u.id)
      );

    if (locationsError) {
      console.error('Error fetching user locations:', locationsError);
      throw new Error(locationsError.message);
    }

    // Combine users with their locations
    type UserLocationResult = { user_id: string; locations: { id: string; name: string; short_code: string } | null };
    const usersWithLocations: UserWithLocations[] = users.map((user) => {
      const userLocs = (userLocations as UserLocationResult[])
        // @ts-expect-error - Database types not regenerated yet
        .filter((ul) => ul.user_id === user.id)
        .map((ul) => ul.locations)
        .filter((loc): loc is { id: string; name: string; short_code: string } => loc !== null);

      return {
        // @ts-expect-error - Database types not regenerated yet
        ...user,
        locations: userLocs,
      };
    });

    return usersWithLocations as UserWithLocations[];
  },

  /**
   * Fetch a single user with location assignments
   */
  async getUser(id: string): Promise<UserWithLocations> {
    const { data: user, error: userError } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();

    if (userError) {
      console.error('Error fetching user:', userError);
      throw new Error(userError.message);
    }

    // Fetch user's locations
    const { data: userLocations, error: locationsError } = await supabase
      .from('user_locations')
      .select(
        `
        locations (
          id,
          name,
          short_code
        )
      `
      )
      .eq('user_id', id);

    if (locationsError) {
      console.error('Error fetching user locations:', locationsError);
      throw new Error(locationsError.message);
    }

    type LocationResult = { locations: { id: string; name: string; short_code: string } | null };
    return {
      // @ts-expect-error - Database types not regenerated yet
      ...user,
      locations: (userLocations as LocationResult[])
        .map((ul) => ul.locations)
        .filter((loc): loc is { id: string; name: string; short_code: string } => loc !== null),
    } as UserWithLocations;
  },

  /**
   * Create a new user via Edge Function
   * This invokes the invite-user Edge Function which:
   * 1. Creates auth user with Supabase Admin API
   * 2. Creates user profile
   * 3. Assigns locations
   * 4. Sends branded invitation email via Resend
   */
  async createUser(input: CreateUserInput): Promise<User> {
    const { data, error } = await supabase.functions.invoke('invite-user', {
      body: {
        email: input.email,
        name: input.name,
        location_ids: input.location_ids,
        is_admin: input.is_admin ?? false,
      },
    });

    if (error) {
      console.error('Error inviting user:', error);
      throw new Error(error.message || 'Failed to invite user');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to invite user');
    }

    // Fetch and return the created user profile
    const { data: user, error: fetchError } = await supabase
      .from('users')
      .select('*')
      .eq('id', data.user_id)
      .single();

    if (fetchError) {
      console.error('Error fetching created user:', fetchError);
      // User was created successfully, just can't fetch it
      // Return a minimal user object
      return {
        id: data.user_id,
        email: input.email,
        name: input.name,
        phone_number: null,
        sms_opted_in_at: null,
        is_active: true,
        is_admin: input.is_admin ?? false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
    }

    return user as User;
  },

  /**
   * Update user profile
   */
  async updateUser(input: UpdateUserInput): Promise<User> {
    const updateData: Partial<User> = {};

    if (input.name !== undefined) updateData.name = input.name;
    if (input.is_active !== undefined) updateData.is_active = input.is_active;
    if (input.is_admin !== undefined) updateData.is_admin = input.is_admin;

    const { data, error } = await supabase
      .from('users')
      // @ts-expect-error - Supabase type mismatch
      .update(updateData)
      .eq('id', input.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating user:', error);
      throw new Error(error.message);
    }

    return data as User;
  },

  /**
   * Update user location assignments
   */
  async updateUserLocations(
    input: UpdateUserLocationsInput
  ): Promise<void> {
    // Delete existing location assignments
    const { error: deleteError } = await supabase
      .from('user_locations')
      .delete()
      .eq('user_id', input.user_id);

    if (deleteError) {
      console.error('Error deleting user locations:', deleteError);
      throw new Error(deleteError.message);
    }

    // Insert new location assignments
    if (input.location_ids.length > 0) {
      const locationAssignments = input.location_ids.map((location_id) => ({
        user_id: input.user_id,
        location_id,
      }));

      const { error: insertError } = await supabase
        .from('user_locations')
        // @ts-expect-error - Database types not regenerated yet
        .insert(locationAssignments);

      if (insertError) {
        console.error('Error inserting user locations:', insertError);
        throw new Error(insertError.message);
      }
    }
  },

  /**
   * Deactivate a user
   */
  async deactivateUser(id: string): Promise<User> {
    return this.updateUser({ id, is_active: false });
  },

  /**
   * Activate a user
   */
  async activateUser(id: string): Promise<User> {
    return this.updateUser({ id, is_active: true });
  },

  /**
   * Send password reset email via Edge Function (branded)
   */
  async sendPasswordReset(email: string): Promise<void> {
    const { data, error } = await supabase.functions.invoke('send-password-reset', {
      body: { email },
    });

    if (error) {
      console.error('Error sending password reset:', error);
      throw new Error(error.message || 'Failed to send password reset');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to send password reset');
    }
  },
};
